import { useAuthentication } from '@bestseller-bit/frontend-community.utilities.authentication';
import { datadogRum as datadogRumImport, RumInitConfiguration } from '@datadog/browser-rum';
import { useEffect } from 'react';

export function removeParamFromHash(url: string, param: string): string {
    const parsedURL = new URL(url);
    const parsedHash = new URLSearchParams(parsedURL.hash.replace('#', ''));
    if (parsedHash.has(param)) {
        parsedHash.delete(param);
    }
    parsedURL.hash = parsedHash.toString() ? `#${parsedHash.toString()}` : '';
    return parsedURL.toString();
}

type UseDataDogParams = Required<
    Pick<RumInitConfiguration, 'applicationId' | 'clientToken' | 'env' | 'service' | 'version'>
>;

/**
 * Shamelessly stolen from https://github.com/BESTSELLER/bestone-buying-ui-library/tree/master/src/hooks/useDataDog
 *
 * @description This hook can be used to initialise communication with DataDog.
 * @param {string} [applicationId] - The id of the target application
 * @param {string} [clientToken] - The client token issued by DataDog
 * @param {string} [env] - The environment of the target application
 * @param {string} [service] - The name of the target application (usually declared in package.json)
 * @param {string} [version] - The version of the target application (usually declared in package.json)
 * @param {boolean=} [includeReplay] - Whether or not to record sessions for session replay
 * @example
 * ```ts
 * import packageJson from 'path/to/package.json';
 *
 * // Basic initialisation of DataDog
 * useDataDog({
 *   applicationId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
 *   clientToken: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
 *   env: process.env.NODE_ENV,
 *   service: packageJson.name,
 *   version: packageJson.version,
 * });
 *
 * // Initialisation and setting user afterwards
 * const { setDataDogUser } = useDataDog({
 *   applicationId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
 *   clientToken: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
 *   env: process.env.NODE_ENV,
 *   service: packageJson.name,
 *   version: packageJson.version,
 * });
 *
 * setDataDogUser(token, user);
 *
 * // Initialisation with access to the datadogRum instance
 * const { datadogRum } = useDataDog({
 *   applicationId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
 *   clientToken: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
 *   env: process.env.NODE_ENV,
 *   service: packageJson.name,
 *   version: packageJson.version,
 * });
 *
 * datadogRum.addAction('Test log', { testContext: 'someValue' });
 * // Documentation (datadogRum):
 * // Custom actions: https://docs.datadoghq.com/real_user_monitoring/browser/tracking_user_actions/?tab=npm#custom-actions
 * // Custom errors:  https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#collect-errors-manually
 * ```
 */
export const useDataDog = ({
    applicationId,
    clientToken,
    env,
    service,
    version,
}: UseDataDogParams): {
    datadogRum: typeof datadogRumImport;
} => {
    const { account } = useAuthentication();
    useEffect(() => {
        // Only disable datadog on localhost
        if (globalThis.location.host.includes('localhost')) {
            return;
        }

        if (!datadogRumImport.getInitConfiguration()) {
            datadogRumImport.init({
                site: 'datadoghq.eu',
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                allowedTracingUrls: [globalThis.origin],
                silentMultipleInit: true,
                applicationId,
                clientToken,
                env,
                service,
                version,
                sessionSampleRate: 100,
                sessionReplaySampleRate: 100,
                defaultPrivacyLevel: 'allow',
                beforeSend: (event) => {
                    event.context = {
                        ...event.context,
                        screenSize: `${globalThis.screen.width}x${globalThis.screen.height}`,
                        viewportSize: `${document.documentElement.clientWidth}x${document.documentElement.clientHeight}`,
                    };

                    // Remove the code value in the hash
                    event.view.url = removeParamFromHash(event.view.url, 'code');
                    return true;
                },
            });

            datadogRumImport.startSessionReplayRecording();
        }

        if (account) {
            datadogRumImport.setUser({
                name: account.name,
                email: account.username ?? account.idTokenClaims?.upn,
                department: account.idTokenClaims?.department,
                departmentnumber: account.idTokenClaims?.departmentnumber,
                jobtitle: account.idTokenClaims?.jobtitle,
            });
        }
    }, [account, applicationId, clientToken, env, service, version]);

    return { datadogRum: datadogRumImport };
};
